import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';

interface TimelineEvent {
  event_type: string;
  title: string;
  description?: string;
  created_at: string;
  metadata: Record<string, any>;
}

interface TimelineResponse {
  user_id: number;
  profile_id?: string;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  is_premium_member: boolean;
  is_photo_verified: boolean;
  is_id_verified: boolean;
  event_count: number;
  events: TimelineEvent[];
}

interface UserSession {
  id: number;
  created_at?: string;
  last_activity_at?: string;
  expires_at?: string;
  is_active: boolean;
  revoked_at?: string | null;
  revoked_reason?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
}

const roleOptions = ['USER', 'SUPER_ADMIN', 'CONTENT_MODERATOR', 'FINANCE_ADMIN', 'SUPPORT_ADMIN', 'ID_VERIFICATION_ADMIN'];

const AdminUserTimelinePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [queryUserId, setQueryUserId] = useState('');
  const [targetRole, setTargetRole] = useState('USER');
  const [loading, setLoading] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [revokingSessions, setRevokingSessions] = useState(false);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [sessions, setSessions] = useState<UserSession[]>([]);

  const resolveUserIdFromSearch = async (searchValue: string): Promise<number | null> => {
    const trimmed = searchValue.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d+$/.test(trimmed)) {
      return Number(trimmed);
    }

    const response = await api.get('/users/admin/users', {
      params: {
        search: trimmed,
        active_status: 'ALL',
        review_status: 'ALL',
      },
    });

    const items = response.data?.items || [];
    if (!items.length) {
      return null;
    }

    const exactProfileMatch = items.find(
      (item: any) => String(item.profile_id || '').toUpperCase() === trimmed.toUpperCase(),
    );
    const candidate = exactProfileMatch || items[0];
    return candidate?.user_id || null;
  };

  const fetchTimeline = async (userIdOverride?: string) => {
    const queryValue = (userIdOverride || queryUserId).trim();
    if (!queryValue) {
      toast.error('Enter user ID or profile ID');
      return;
    }

    try {
      setLoading(true);
      const resolvedUserId = await resolveUserIdFromSearch(queryValue);
      if (!resolvedUserId) {
        toast.error('No user found for this search');
        setTimeline(null);
        return;
      }

      const response = await api.get(`/users/admin/users/${resolvedUserId}/timeline`);
      setTimeline(response.data);
      setTargetRole(response.data.role || 'USER');
      setQueryUserId(String(response.data.profile_id || resolvedUserId));
      setSessionsLoading(true);
      try {
        const sessionsResponse = await api.get(`/users/admin/users/${resolvedUserId}/sessions`);
        setSessions(sessionsResponse.data.items || []);
      } catch (sessionError: any) {
        setSessions([]);
        toast.error(sessionError.response?.data?.detail || 'Failed to load user sessions');
      } finally {
        setSessionsLoading(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to load timeline');
      setTimeline(null);
    } finally {
      setLoading(false);
    }
  };

  const revokeSession = async (sessionId: number) => {
    if (!timeline) return;
    try {
      setRevokingSessions(true);
      await api.post(`/users/admin/users/${timeline.user_id}/sessions/${sessionId}/revoke`, null, { params: { reason: 'Revoked by super administrator' } });
      toast.success('Session revoked');
      await fetchTimeline();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to revoke session');
    } finally {
      setRevokingSessions(false);
    }
  };

  const revokeAllSessions = async () => {
    if (!timeline) return;
    if (!window.confirm(`Force logout ${timeline.name} from all active devices?`)) return;
    try {
      setRevokingSessions(true);
      const response = await api.post(`/users/admin/users/${timeline.user_id}/sessions/revoke-all`, null, { params: { reason: 'Forced logout by super administrator' } });
      toast.success(`${response.data.revoked_count || 0} session(s) revoked`);
      await fetchTimeline();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to revoke user sessions');
    } finally {
      setRevokingSessions(false);
    }
  };

  React.useEffect(() => {
    const prefillUserId = searchParams.get('userId') || '';
    if (prefillUserId) {
      setQueryUserId(prefillUserId);
      fetchTimeline(prefillUserId);
    }
  }, [searchParams]);

  const updateRole = async () => {
    if (!timeline) return;
    try {
      setSavingRole(true);
      await api.post(`/users/admin/users/${timeline.user_id}/set-role`, null, { params: { role: targetRole } });
      toast.success('User role updated');
      await fetchTimeline();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const deleteUser = async () => {
    if (!timeline) return;
    const confirmed = window.confirm(`Delete user ${timeline.name} permanently? This cannot be undone.`);
    if (!confirmed) return;

    try {
      setDeleting(true);
      await api.delete(`/users/admin/users/${timeline.user_id}`);
      toast.success('User deleted');
      setTimeline(null);
      setQueryUserId('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  return (
    <AdminLayout currentPage="user-timeline">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Super Admin User Control Center
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Inspect full activity timeline, update role permissions, and perform irreversible user cleanup.
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 200px 1fr' }, gap: 1.25 }}>
            <TextField
              label="User ID / Profile ID / Email / Phone"
              size="small"
              value={queryUserId}
              onChange={(e) => setQueryUserId(e.target.value)}
            />
            <Button variant="contained" onClick={() => fetchTimeline()} disabled={loading}>
              {loading ? 'Loading...' : 'Load Timeline'}
            </Button>
            <Alert severity="info">Use this for deep moderation audits and super-admin actions.</Alert>
          </Box>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : timeline ? (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{timeline.name}</Typography>
              <Typography variant="body2">Profile ID: {timeline.profile_id || '-'}</Typography>
              <Typography variant="body2">Email: {timeline.email}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Role: ${timeline.role}`} color="primary" />
                <Chip size="small" label={timeline.is_active ? 'Active' : 'Inactive'} color={timeline.is_active ? 'success' : 'default'} />
                <Chip size="small" label={timeline.is_premium_member ? 'Premium' : 'Standard'} color={timeline.is_premium_member ? 'warning' : 'default'} />
                <Chip size="small" label={timeline.is_id_verified ? 'ID Verified' : 'ID Pending'} color={timeline.is_id_verified ? 'success' : 'default'} />
                <Chip size="small" label={timeline.is_photo_verified ? 'Photo Verified' : 'Photo Pending'} color={timeline.is_photo_verified ? 'success' : 'default'} />
                <Chip size="small" label={`Events: ${timeline.event_count}`} />
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.2, flexWrap: 'wrap' }}>
                <Box><Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Active Sessions & Devices</Typography><Typography variant="body2" color="textSecondary">Admins can revoke access without viewing any token or password.</Typography></Box>
                <Button color="error" variant="outlined" onClick={revokeAllSessions} disabled={revokingSessions || !sessions.some((session) => session.is_active)}>Force Logout All Devices</Button>
              </Box>
              {sessionsLoading ? <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box> : sessions.length === 0 ? <Alert severity="info">No server-side sessions recorded for this user yet. New login sessions are tracked automatically.</Alert> : <TableContainer component={Paper} elevation={0}><Table size="small"><TableHead><TableRow><TableCell><strong>Status</strong></TableCell><TableCell><strong>Last Activity (IST)</strong></TableCell><TableCell><strong>Created (IST)</strong></TableCell><TableCell><strong>IP Address</strong></TableCell><TableCell><strong>Device</strong></TableCell><TableCell><strong>Action</strong></TableCell></TableRow></TableHead><TableBody>{sessions.map((session) => <TableRow key={session.id}><TableCell><Chip size="small" label={session.is_active ? 'Active' : 'Revoked / Expired'} color={session.is_active ? 'success' : 'default'} /></TableCell><TableCell>{formatDate(session.last_activity_at || '')}</TableCell><TableCell>{formatDate(session.created_at || '')}</TableCell><TableCell>{session.ip_address || '-'}</TableCell><TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={session.user_agent || ''}>{session.user_agent || '-'}</TableCell><TableCell>{session.is_active ? <Button size="small" color="error" variant="outlined" onClick={() => revokeSession(session.id)} disabled={revokingSessions}>Revoke</Button> : <Typography variant="caption">{session.revoked_reason || '-'}</Typography>}</TableCell></TableRow>)}</TableBody></Table></TableContainer>}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Super Admin Actions</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '260px 180px auto' }, gap: 1.25 }}>
                <TextField
                  select
                  size="small"
                  label="Set Role"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                >
                  {roleOptions.map((role) => (
                    <MenuItem key={role} value={role}>{role}</MenuItem>
                  ))}
                </TextField>
                <Button variant="outlined" onClick={updateRole} disabled={savingRole}>
                  {savingRole ? 'Updating...' : 'Update Role'}
                </Button>
                <Button color="error" variant="contained" onClick={deleteUser} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Hard Delete User'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>Activity Timeline</Typography>
              {timeline.events.length === 0 ? (
                <Alert severity="info">No activity found for this user.</Alert>
              ) : (
                <TableContainer component={Paper} elevation={0}>
                  <Table sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Time (IST)</strong></TableCell>
                        <TableCell><strong>Type</strong></TableCell>
                        <TableCell><strong>Title</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell><strong>Metadata</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {timeline.events.map((event, idx) => (
                        <TableRow key={`${event.event_type}-${event.created_at}-${idx}`}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(event.created_at)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={event.event_type} />
                          </TableCell>
                          <TableCell>{event.title}</TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>{event.description || '-'}</TableCell>
                          <TableCell sx={{ maxWidth: 380, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                            {Object.entries(event.metadata || {}).map(([k, v]) => `${k}: ${String(v)}`).join(' | ') || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Stack>
      ) : (
        <Alert severity="info">Search a user to open timeline and super-admin controls.</Alert>
      )}
    </AdminLayout>
  );
};

export default AdminUserTimelinePage;
